import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  tableCellClasses,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import { setApiError } from '../../store/Slice/Error/ErrorSlice';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import ApiErrorMessage from '../Global/ApiErrorMessage';
import { NOT_AVAILABLE } from '../Global/Constants';
import Loader from '../Global/Loader';
import CallCenterService from '../services/CallCenterService';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';

const { GetAllAssignableStatuses, UpdateStatus } = CallCenterService;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0
  }
}));
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 12,
    padding: '6px',
    height: '42.5px',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: '#0d4689'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 11,
    padding: '4px 6px',
    height: '42.5px'
  }
}));

export default function UpdateBankops() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();

  const [openFollowUp, setOpenFollowUp] = useState({ status: false, indexVal: null });
  const [openSanctionedFollowUp, setOpenSanctionedFollowUp] = useState({
    status: false,
    indexVal: null
  });

  const [updateStatusList, setUpdateStatusList] = useState([]);
  const [selectedItemList, setSelectedItemList] = useState([]);
  const [openApiError, setOpenApiError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [validationMsg, setValidationMsg] = useState('');
  const [openLoader, setOpenLoader] = useState(false);

  const updateItem = useCallback((index, field, value) => {
    setSelectedItemList((prev) => {
      const next = [...prev];
      if (typeof field === 'object') next[index] = { ...next[index], ...field };
      else next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const handleStatus = useCallback(
    (e, index) => {
      const val = e.target.value;
      if (JSON.parse(val).stageStatusName === 'Login') {
        setOpenFollowUp({ status: true, indexVal: index });
      }
      if (JSON.parse(val).stageStatusName.toLowerCase() === 'sanctioned') {
        setOpenSanctionedFollowUp({ status: true, indexVal: index });
      }
      updateItem(index, 'status', val);
    },
    [updateItem]
  );

  const handleUpdate = async () => {
    const objArray = selectedItemList
      .filter((i) => i.status !== ' ')
      .map((item) => {
        const parsedStatus = JSON.parse(item.status);
        const isLogin = parsedStatus.stageStatusName === 'Login';
        return {
          leadId: item.leadId,
          stageCode: 'BANKOPS',
          stageStatusId: parsedStatus.stageStatusId,
          userId: LoginData?.userId,
          applicationId: item.applicationId,
          comments: isLogin ? item.bankOpsLoginDetailsModel.comments : item.comments,
          ...(isLogin && {
            bankOpsLoginDetailsModel: {
              fileNumber: item.bankOpsLoginDetailsModel.fileNumber,
              comments: item.bankOpsLoginDetailsModel.comments
            }
          })
        };
      });

    if (objArray.length === selectedItemList.length) {
      const res = await UpdateStatus(objArray, LoginData?.jwt);
      if (res.status === 'success') {
        dispatch(setApiError(res?.msg || 'Update Status updated successfully'));
        navigate('/bankops');
      } else {
        setOpenApiError(true);
        setErrorMessage('Something went wrong!');
      }
    } else {
      setDialogOpen(true);
      setValidationMsg('Please select status');
    }
  };

  useEffect(() => {
    const fetchStatuses = async () => {
      if (!LoginData) return;
      const res = await GetAllAssignableStatuses('BANKOPS', LoginData.jwt);
      if (res.status === 'success') {
        const local = JSON.parse(localStorage.getItem('selectedItem')) || [];
        const processedList = res.data.map((statusItem) => ({
          ...statusItem,
          leadStatus: !local.some((listItem) => {
            const statusName = statusItem.stageStatusName.toLowerCase();
            const logInStatus = listItem.logInStatusName.toLowerCase();
            if (
              logInStatus === 'u-login' &&
              ['u-sanction', 'sanctioned', 'rejected'].includes(statusName)
            )
              return true;
            if (logInStatus === 'login' && ['sanctioned', 'rejected', 'login'].includes(statusName))
              return true;
            if (logInStatus === 'u-sanction' && !['sanctioned', 'rejected'].includes(statusName))
              return true;
            return false;
          })
        }));
        setUpdateStatusList(processedList);
        setSelectedItemList(
          local.map((item) => ({
            ...item,
            status: ' ',
            comments: '',
            bankOpsLoginDetailsModel: { fileNumber: '', comments: '', isSubmitClicked: false }
          }))
        );
      }
    };
    fetchStatuses();
  }, [LoginData]);
  const handleFollowUpSubmit = useCallback(
    (_, index) => {
      const item = selectedItemList[index];
      if (!item.bankOpsLoginDetailsModel.fileNumber) {
        setValidationMsg('Please enter file number');
        setDialogOpen(true);
        return;
      }

      if (!item.bankOpsLoginDetailsModel.comments) {
        setValidationMsg('Please enter comments');
        setDialogOpen(true);
        return;
      }
      updateItem(index, 'bankOpsLoginDetailsModel', {
        ...item.bankOpsLoginDetailsModel,
        isSubmitClicked: true
      });
      setOpenFollowUp({ status: false, indexVal: null });
    },
    [selectedItemList, updateItem]
  );

  const handleFollowUpCloseIcon = useCallback(() => {
    const idx = openFollowUp.indexVal;
    if (idx !== null) {
      const item = selectedItemList[idx];
      if (
        !(
          item.bankOpsLoginDetailsModel.fileNumber &&
          item.bankOpsLoginDetailsModel.comments &&
          item.bankOpsLoginDetailsModel.isSubmitClicked
        )
      ) {
        updateItem(idx, 'status', ' ');
      }
    }
    setOpenFollowUp({ status: false, indexVal: null });
  }, [openFollowUp, selectedItemList, updateItem]);

  return (
    <Page title="Partner Management System || Bankops">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Update Bankops Status" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Box className="tableBox" mb={3}>
            <TableContainer sx={{ minWidth: 800, overflow: 'auto', background: 'white' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Lead ID</StyledTableCell>
                    <StyledTableCell>Lead Name</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>Comments</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedItemList.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{item.leadId || NOT_AVAILABLE}</StyledTableCell>
                      <StyledTableCell>{item.leadName || NOT_AVAILABLE}</StyledTableCell>
                      <StyledTableCell>
                        <Select
                          fullWidth
                          inputProps={{
                            className: `textInput textInputTable customSelectBox dissabledMenu${item.status}`
                          }}
                          value={item.status}
                          onChange={(e) => handleStatus(e, index)}
                          sx={{ minWidth: 200 }}
                        >
                          <MenuItem value=" " disabled>
                            Select Status
                          </MenuItem>
                          {updateStatusList.map((s, idx) => (
                            <MenuItem key={idx} disabled={!s.leadStatus} value={JSON.stringify(s)}>
                              {s.stageStatusName}
                            </MenuItem>
                          ))}
                        </Select>
                      </StyledTableCell>
                      <StyledTableCell>
                        <TextField
                          fullWidth
                          inputProps={{ className: 'textInput textInputTable' }}
                          placeholder="Write Your Comments"
                          sx={{ minWidth: 200 }}
                          value={item.bankOpsLoginDetailsModel.comments || item.comments}
                          onChange={(e) => updateItem(index, 'comments', e.target.value)}
                        />
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box textAlign="right">
            <Button variant="contained" className="mainBtn" onClick={handleUpdate}>
              Update
            </Button>
          </Box>

          <Dialog className="PopupBox" open={openFollowUp.status} fullWidth>
            <DialogTitle className="popupTitle">
              <Typography variant="h6">Login Status</Typography>
              <IconButton onClick={handleFollowUpCloseIcon} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl required fullWidth className="formGroup">
                    <Typography variant="h6" className="formLabel">
                      LAN Id
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter LAN Id"
                      inputProps={{ className: 'textInput' }}
                      value={
                        selectedItemList[openFollowUp.indexVal]?.bankOpsLoginDetailsModel
                          .fileNumber || ''
                      }
                      onChange={(e) => {
                        const idx = openFollowUp.indexVal;
                        updateItem(idx, 'bankOpsLoginDetailsModel', {
                          ...selectedItemList[idx].bankOpsLoginDetailsModel,
                          fileNumber: e.target.value
                        });
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl required fullWidth className="formGroup">
                    <Typography variant="h6" className="formLabel">
                      Comments
                    </Typography>
                    <TextField
                      multiline
                      rows={4}
                      fullWidth
                      placeholder="Write Your Comments"
                      InputProps={{ classes: { root: 'textInput' } }}
                      value={
                        selectedItemList[openFollowUp.indexVal]?.bankOpsLoginDetailsModel
                          .comments || ''
                      }
                      onChange={(e) => {
                        const idx = openFollowUp.indexVal;
                        updateItem(idx, 'bankOpsLoginDetailsModel', {
                          ...selectedItemList[idx].bankOpsLoginDetailsModel,
                          comments: e.target.value
                        });
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => handleFollowUpSubmit(null, openFollowUp.indexVal)}
                variant="contained"
                className="mainBtn"
              >
                Submit
              </Button>
            </DialogActions>
          </Dialog>

          <ApiErrorMessage
            openApiError={openApiError}
            setOpenApiError={setOpenApiError}
            errorMessage={errorMessage}
          />
          <Loader openLoader={openLoader} />
          <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
            <DialogContent>
              <Typography>{validationMsg}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Retry</Button>
            </DialogActions>
          </Dialog>

          <Dialog
            className="PopupBox"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            open={openSanctionedFollowUp.status}
            fullWidth
            onClose={(e) => {
              handleFollowUpCloseIcon(e);
            }}
          >
            <DialogTitle className="popupTitle">
              <Typography variant="h6">Sanctioned</Typography>
              <IconButton
                onClick={(e) => {
                  handleFollowUpCloseIcon(e);
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box mb={2}>
                <FormControl required fullWidth className="formGroup">
                  <Typography variant="body1" className="formLabel">
                    Sanctioned Amount
                  </Typography>
                  <TextField
                    inputProps={{ className: 'textInput' }}
                    fullWidth
                    placeholder="Sanctioned Amount"
                    value={
                      selectedItemList[openFollowUp.indexVal]?.disbursementDisburseDetailsModel
                        ?.disbursedAmount
                    }
                    type="number"
                  />
                </FormControl>
              </Box>
              <Button
                variant="contained"
                className="mainBtn mainBtnSec mainBtnOther mb-2"
                component="label"
              >
                Upload supporting document
                <input
                  hidden
                  accept="image/png, image/gif, image/jpeg, image/jpg"
                  type="file"
                  onChange={(e) => {
                    const fileData = e.target.files[0];
                  }}
                />
              </Button>
              <Box className="">
                <Typography variant="body2" mb={0.5}>
                  <strong>List of document uploaded </strong>
                </Typography>
                {selectedItemList[openFollowUp.indexVal]?.disbursementDisburseDetailsModel
                  ?.disbursementDisburseDocumentsDetailsModelList.length > 0 ? (
                  selectedItemList[
                    openFollowUp.indexVal
                  ]?.disbursementDisburseDetailsModel?.disbursementDisburseDocumentsDetailsModelList.map(
                    (fileTtem, fileTtemIndex) => (
                      <Typography key={fileTtemIndex} variant="body2">
                        <span style={{ width: '100%', float: 'left' }}>
                          <span style={{ float: 'left', paddingRight: '5px' }}>
                            {fileTtem.fileName}
                          </span>{' '}
                          <CancelIcon
                            style={{
                              marginTop: '3px',
                              float: 'left',
                              fontSize: '15px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              const prevArray = selectedItemList;
                              prevArray[
                                openFollowUp.indexVal
                              ].disbursementDisburseDetailsModel.disbursementDisburseDocumentsDetailsModelList.splice(
                                fileTtemIndex,
                                1
                              );
                              setSelectedItemList([...prevArray]);
                            }}
                          />
                        </span>
                      </Typography>
                    )
                  )
                ) : (
                  <Typography variant="body2">No Document Uploaded</Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenSanctionedFollowUp(false)}
                variant="contained"
                className="mainBtn"
              >
                Submit
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Page>
  );
}
